import {
  ArchiveJournalEntryReq,
  ArchiveJournalEntryRes,
  BulkUpsertAccountsReq,
  BulkUpsertAccountsRes,
  BulkUpsertMappingsReq,
  BulkUpsertMappingsRes,
  CreateAccountReq,
  CreateAccountRes,
  CreateExternalTransactionReq,
  CreateExternalTransactionRes,
  DeactivateAccountReq,
  DeactivateAccountRes,
  DownloadJournalEntryCSVReq,
  DownloadJournalEntryCSVRes,
  ExportJournalEntryToQuickbooksOnlineReq,
  ExportJournalEntryToQuickbooksOnlineRes,
  GenerateJournalEntriesV2Req,
  GenerateJournalEntriesV2Res,
  GenerateJournalEntryForOrderShipmentReq,
  GenerateJournalEntryForOrderShipmentRes,
  GetExternalAccountBalanceReq,
  GetExternalAccountBalanceRes,
  GetExternalTransactionReq,
  GetExternalTransactionRes,
  GetJournalEntryQuickbooksDesktopIIFReq,
  GetJournalEntryQuickbooksDesktopIIFRes,
  GetJournalEntryReq,
  GetJournalEntryRes,
  GetJournalEntrySage100CSVReq,
  GetJournalEntrySage100CSVRes,
  ListAccountsReq,
  ListAccountsRes,
  ListMappingsReq,
  ListMappingsRes,
  ReactivateAccountReq,
  ReactivateAccountRes,
  UpdateAccountReq,
  UpdateAccountRes,
  UpdateExternalTransactionReq,
  UpdateExternalTransactionRes,
  UpdateMappingAccountReq,
  UpdateMappingAccountRes,
  VoidExternalTransactionReq,
  VoidExternalTransactionRes,
} from "gen/api/accounting/service_pb"
import { AccountingServiceClient } from "gen/api/accounting/service_pb.client"
import { UUID } from "shared-ts/classes/uuid"

import { apiSlice, transport } from "./apiSlice"

const a = new AccountingServiceClient(transport)

const extendedApiSlice = apiSlice
  .enhanceEndpoints({
    addTagTypes: [
      "Account",
      "ExternalAccountBalance",
      "JournalEntries",
      "OrderShipment",
      "ShipmentCountsByStatus",
      "Mapping",
      "JournalEntry",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Account
      createAccount: builder.mutation<CreateAccountRes, CreateAccountReq>({
        query: (r) => a.createAccount(r),
        invalidatesTags: ["Account"],
      }),
      deactivateAccount: builder.mutation<
        DeactivateAccountRes,
        DeactivateAccountReq
      >({
        query: (r) => a.deactivateAccount(r),
        invalidatesTags: ["Account"],
      }),
      reactivateAccount: builder.mutation<
        ReactivateAccountRes,
        ReactivateAccountReq
      >({
        query: (r) => a.reactivateAccount(r),
        invalidatesTags: ["Account"],
      }),
      listAccounts: builder.query<ListAccountsRes, ListAccountsReq>({
        query: (r) => a.listAccounts(r),
        providesTags: ["Account"],
      }),
      updateAccount: builder.mutation<UpdateAccountRes, UpdateAccountReq>({
        query: (r) => a.updateAccount(r),
        invalidatesTags: ["Account"],
      }),
      bulkUpsertAccounts: builder.mutation<
        BulkUpsertAccountsRes,
        BulkUpsertAccountsReq
      >({
        query: (r) => a.bulkUpsertAccounts(r),
        invalidatesTags: ["Account"],
      }),
      listMappings: builder.query<ListMappingsRes, ListMappingsReq>({
        query: (r) => a.listMappings(r),
        providesTags: ["Mapping"],
      }),
      updateMappingAccount: builder.mutation<
        UpdateMappingAccountRes,
        UpdateMappingAccountReq
      >({
        query: (r) => a.updateMappingAccount(r),
        invalidatesTags: ["Mapping"],
      }),
      bulkUpsertMappings: builder.mutation<
        BulkUpsertMappingsRes,
        BulkUpsertMappingsReq
      >({
        query: (r) => a.bulkUpsertMappings(r),
        invalidatesTags: ["Mapping"],
      }),
      // External Transactions
      getExternalAccountBalance: builder.query<
        GetExternalAccountBalanceRes,
        GetExternalAccountBalanceReq
      >({
        query: (r) => a.getExternalAccountBalance(r),
        providesTags: ["ExternalAccountBalance"],
      }),
      createExternalTransaction: builder.mutation<
        CreateExternalTransactionRes,
        CreateExternalTransactionReq
      >({
        query: (r) => a.createExternalTransaction(r),
        invalidatesTags: (_response, _error, request) => [
          "ExternalAccountBalance",
          "ShipmentCountsByStatus",
          ...(request.orderShipmentIds?.map((id) => ({
            type: "OrderShipment" as const,
            id: UUID.fromPB(id).toString(),
          })) ?? []),
        ],
      }),
      updateExternalTransaction: builder.mutation<
        UpdateExternalTransactionRes,
        UpdateExternalTransactionReq
      >({
        query: (r) => a.updateExternalTransaction(r),
        invalidatesTags: ["ExternalAccountBalance", "ShipmentCountsByStatus"],
      }),
      voidExternalTransaction: builder.mutation<
        VoidExternalTransactionRes,
        VoidExternalTransactionReq
      >({
        query: (r) => a.voidExternalTransaction(r),
        invalidatesTags: ["ExternalAccountBalance", "ShipmentCountsByStatus"],
      }),
      getExternalTransaction: builder.query<
        GetExternalTransactionRes,
        GetExternalTransactionReq
      >({
        query: (r) => a.getExternalTransaction(r),
      }),
      getJournalEntry: builder.query<GetJournalEntryRes, GetJournalEntryReq>({
        query: (r) => a.getJournalEntry(r),
        providesTags: (_response, _error, request) => [
          {
            type: "JournalEntry",
            id: UUID.idString(request.id),
          },
        ],
      }),
      generateJournalEntriesV2: builder.mutation<
        GenerateJournalEntriesV2Res,
        GenerateJournalEntriesV2Req
      >({
        query: (r) => a.generateJournalEntriesV2(r),
      }),
      generateJournalEntryForOrderShipment: builder.mutation<
        GenerateJournalEntryForOrderShipmentRes,
        GenerateJournalEntryForOrderShipmentReq
      >({
        query: (r) => a.generateJournalEntryForOrderShipment(r),
      }),
      exportJournalEntryToQuickbooksOnline: builder.mutation<
        ExportJournalEntryToQuickbooksOnlineRes,
        ExportJournalEntryToQuickbooksOnlineReq
      >({
        query: (r) => a.exportJournalEntryToQuickbooksOnline(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "JournalEntry",
            id: UUID.idString(request.id),
          },
        ],
      }),
      getJournalEntryQuickbooksDesktopIIF: builder.mutation<
        GetJournalEntryQuickbooksDesktopIIFRes,
        GetJournalEntryQuickbooksDesktopIIFReq
      >({
        query: (r) => a.getJournalEntryQuickbooksDesktopIIF(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "JournalEntry",
            id: UUID.idString(request.id),
          },
        ],
      }),
      getJournalEntrySage100CSV: builder.mutation<
        GetJournalEntrySage100CSVRes,
        GetJournalEntrySage100CSVReq
      >({
        query: (r) => a.getJournalEntrySage100CSV(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "JournalEntry",
            id: UUID.idString(request.id),
          },
        ],
      }),
      archiveJournalEntry: builder.mutation<
        ArchiveJournalEntryRes,
        ArchiveJournalEntryReq
      >({
        query: (r) => a.archiveJournalEntry(r),
        invalidatesTags: (_response, _error, request) => [
          {
            type: "JournalEntry",
            id: UUID.idString(request.id),
          },
        ],
      }),
      downloadJournalEntryCSV: builder.query<
        DownloadJournalEntryCSVRes,
        DownloadJournalEntryCSVReq
      >({
        query: (r) => a.downloadJournalEntryCSV(r),
      }),
    }),
  })

export const {
  useCreateAccountMutation,
  useDeactivateAccountMutation,
  useReactivateAccountMutation,
  useListAccountsQuery,
  useLazyListAccountsQuery,
  useUpdateAccountMutation,
  useBulkUpsertAccountsMutation,
  useListMappingsQuery,
  useUpdateMappingAccountMutation,
  useBulkUpsertMappingsMutation,
  useGetExternalAccountBalanceQuery,
  useLazyGetExternalAccountBalanceQuery,
  useCreateExternalTransactionMutation,
  useUpdateExternalTransactionMutation,
  useVoidExternalTransactionMutation,
  useGetExternalTransactionQuery,
  useGetJournalEntryQuery,
  useGenerateJournalEntriesV2Mutation,
  useGenerateJournalEntryForOrderShipmentMutation,
  useExportJournalEntryToQuickbooksOnlineMutation,
  useGetJournalEntryQuickbooksDesktopIIFMutation,
  useGetJournalEntrySage100CSVMutation,
  useArchiveJournalEntryMutation,
  useLazyDownloadJournalEntryCSVQuery,
} = extendedApiSlice
